/**
 * Google Drive Service
 * Handles OAuth authentication, token management, and Google Picker integration
 */

declare global {
    interface Window {
        gapi: any;
        google: any;
    }
}

// Google Identity Services types
interface TokenResponse {
    access_token: string;
    expires_in: number;
    scope: string;
    token_type: string;
    error?: string;
}

export interface GoogleDriveFile {
    id: string;
    name: string;
    mimeType: string;
    webViewLink?: string;
    webContentLink?: string;
}

export interface GoogleDriveToken {
    access_token: string;
    expires_in: number;
    refresh_token?: string;
    scope: string;
    token_type: string;
    expiry_date?: number;
}

export class GoogleDriveService {
    private clientId: string;
    private apiKey: string;
    private discoveryDocs: string[] = ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'];
    // Use drive.file scope: access only to files the app creates or user selects via Google Picker
    private scopes: string = 'https://www.googleapis.com/auth/drive.file';
    private pickerApiLoaded: boolean = false;
    private gapiLoaded: boolean = false;
    private tokenStorageKey: string = 'google_drive_token';

    constructor() {
        // Use REACT_APP_ prefix for Create React App
        this.clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
        this.apiKey = process.env.REACT_APP_GOOGLE_API_KEY || '';
        
        console.log('🔧 GoogleDriveService initialized:', {
            hasClientId: !!this.clientId,
            hasApiKey: !!this.apiKey,
            clientIdPreview: this.clientId ? this.clientId.substring(0, 20) + '...' : 'missing',
            apiKeyPreview: this.apiKey ? this.apiKey.substring(0, 10) + '...' : 'missing'
        });
        
        if (!this.clientId || !this.apiKey) {
            console.warn('⚠️ Google Drive: Client ID or API Key not configured');
            console.warn('⚠️ Make sure REACT_APP_GOOGLE_CLIENT_ID and REACT_APP_GOOGLE_API_KEY are set in your .env file');
            console.warn('⚠️ Current env values:', {
                REACT_APP_GOOGLE_CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                REACT_APP_GOOGLE_API_KEY: process.env.REACT_APP_GOOGLE_API_KEY
            });
        }
    }

    /**
     * Load Google APIs (using Google Identity Services instead of deprecated auth2)
     */
    async loadGoogleApis(): Promise<void> {
        if (this.gapiLoaded && this.pickerApiLoaded) {
            return;
        }

        return new Promise((resolve, reject) => {
            // Load Google Identity Services
            const gisScript = document.createElement('script');
            gisScript.src = 'https://accounts.google.com/gsi/client';
            gisScript.onload = () => {
                console.log('✅ Google Identity Services loaded');
                
                // Initialize Google Identity Services
                if (!this.clientId) {
                    reject(new Error('REACT_APP_GOOGLE_CLIENT_ID is not set. Please check your .env file.'));
                    return;
                }

                window.google.accounts.id.initialize({
                    client_id: this.clientId,
                });

                // Load Picker API
                // Check if already loaded
                if (window.google && window.google.picker) {
                    this.pickerApiLoaded = true;
                    this.gapiLoaded = true;
                    console.log('✅ Google Picker API already loaded');
                    resolve();
                    return;
                }

                const pickerScript = document.createElement('script');
                pickerScript.src = 'https://apis.google.com/js/picker.js';
                pickerScript.onload = () => {
                    // Wait a bit for the API to be fully initialized
                    setTimeout(() => {
                        if (window.google && window.google.picker) {
                            this.pickerApiLoaded = true;
                            this.gapiLoaded = true; // Mark as loaded (we're using GIS, not gapi)
                            console.log('✅ Google Picker API loaded and initialized');
                            resolve();
                        } else {
                            console.warn('⚠️ Picker script loaded but API not available yet');
                            // Try to resolve anyway - the waitForPickerApi will handle it
                            this.pickerApiLoaded = true;
                            this.gapiLoaded = true;
                            resolve();
                        }
                    }, 200);
                };
                pickerScript.onerror = () => {
                    reject(new Error('Failed to load Google Picker script'));
                };
                document.head.appendChild(pickerScript);
            };
            gisScript.onerror = () => {
                reject(new Error('Failed to load Google Identity Services script'));
            };
            document.head.appendChild(gisScript);
        });
    }

    /**
     * Get stored token from localStorage
     */
    private getStoredToken(): GoogleDriveToken | null {
        try {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'GoogleDriveService.ts:139',message:'getStoredToken: Entry',data:{storageKey:this.tokenStorageKey},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
            // #endregion
            
            const tokenStr = localStorage.getItem(this.tokenStorageKey);
            
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'GoogleDriveService.ts:142',message:'getStoredToken: localStorage result',data:{hasTokenStr:!!tokenStr,tokenStrLength:tokenStr?.length||0},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
            // #endregion
            
            if (!tokenStr) return null;
            
            const token: GoogleDriveToken = JSON.parse(tokenStr);
            
            // Check if token has the correct scope (drive.file). If token was created with old scope (e.g. drive.readonly), clear it so user re-authenticates with drive.file.
            const hasDriveFileScope = token.scope && (
                token.scope.includes('drive.file') ||
                token.scope.includes('https://www.googleapis.com/auth/drive.file')
            );
            
            if (token.scope && !hasDriveFileScope) {
                console.log('⚠️ Stored token has old scope, needs re-authentication', {
                    currentScope: token.scope,
                    expectedScope: 'https://www.googleapis.com/auth/drive.file'
                });
                this.clearToken();
                return null;
            }
            
            // #region agent log
            const now = Date.now();
            const isExpired = token.expiry_date && token.expiry_date < now;
            fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'GoogleDriveService.ts:144',message:'getStoredToken: Parsed token',data:{hasToken:!!token,hasAccessToken:!!token?.access_token,hasExpiryDate:!!token?.expiry_date,expiryDate:token?.expiry_date,currentTime:now,isExpired:isExpired,minutesUntilExpiry:token?.expiry_date?Math.floor((token.expiry_date-now)/60000):null},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
            // #endregion
            
            // Check if token is expired
            if (token.expiry_date && token.expiry_date < Date.now()) {
                console.log('⏰ Stored token is expired');
                return null;
            }
            
            return token;
        } catch (error) {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'GoogleDriveService.ts:154',message:'getStoredToken: Error',data:{error:error instanceof Error?error.message:String(error)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
            // #endregion
            console.error('❌ Error reading stored token:', error);
            return null;
        }
    }

    /**
     * Store token in localStorage
     */
    private storeToken(token: GoogleDriveToken): void {
        try {
            // Calculate expiry date if not provided
            if (!token.expiry_date && token.expires_in) {
                token.expiry_date = Date.now() + (token.expires_in * 1000);
            }
            
            localStorage.setItem(this.tokenStorageKey, JSON.stringify(token));
            console.log('💾 Token stored in localStorage');
        } catch (error) {
            console.error('❌ Error storing token:', error);
        }
    }

    /**
     * Clear stored token
     */
    private clearToken(): void {
        localStorage.removeItem(this.tokenStorageKey);
    }

    /**
     * Check if user is already authorized
     */
    async checkAuthStatus(): Promise<boolean> {
        const token = this.getStoredToken();
        if (token && token.access_token) {
            // Verify token is still valid by checking expiry
            if (token.expiry_date && token.expiry_date > Date.now()) {
                return true;
            }
        }
        return false;
    }

    /**
     * Request Google Drive access (OAuth flow using Google Identity Services)
     */
    async requestDriveAccess(): Promise<GoogleDriveToken> {
        if (!this.gapiLoaded) {
            await this.loadGoogleApis();
        }

        if (!this.clientId) {
            throw new Error('Google Client ID is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your .env file.');
        }

        return new Promise((resolve, reject) => {
            // Use Google Identity Services to get access token
            const tokenClient = window.google.accounts.oauth2.initTokenClient({
                client_id: this.clientId,
                scope: this.scopes,
                callback: (response: TokenResponse) => {
                    if (response.error) {
                        console.error('❌ OAuth error:', response.error);
                        if (response.error === 'popup_closed_by_user' || response.error === 'access_denied') {
                            reject(new Error('Sign-in cancelled'));
                        } else {
                            reject(new Error(`OAuth error: ${response.error}`));
                        }
                        return;
                    }

                    const token: GoogleDriveToken = {
                        access_token: response.access_token,
                        expires_in: response.expires_in || 3600,
                        scope: response.scope,
                        token_type: response.token_type || 'Bearer'
                    };
                    
                    this.storeToken(token);
                    console.log('✅ Google Drive access granted');
                    resolve(token);
                },
            });

            // Request access token
            tokenClient.requestAccessToken({ prompt: 'consent' });
        });
    }

    /**
     * Get access token (with automatic refresh if needed)
     */
    async getAccessToken(): Promise<string> {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'GoogleDriveService.ts:246',message:'getAccessToken: Entry',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        
        // Check for stored token
        let token = this.getStoredToken();
        
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'GoogleDriveService.ts:248',message:'getAccessToken: getStoredToken result',data:{hasToken:!!token,hasAccessToken:!!token?.access_token,hasExpiryDate:!!token?.expiry_date},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        
        // Add detailed logging for token state
        if (token && token.access_token) {
            const now = Date.now();
            const bufferTime = 5 * 60 * 1000; // 5 minutes
            const isExpired = token.expiry_date && token.expiry_date <= (now + bufferTime);
            
            if (token.expiry_date) {
                const timeUntilExpiry = token.expiry_date - now;
                const minutesUntilExpiry = Math.floor(timeUntilExpiry / 60000);
                const isExpiredNow = token.expiry_date <= now;
                
                console.log('🔍 Token status check:', {
                    hasToken: true,
                    hasExpiryDate: true,
                    expiryDate: new Date(token.expiry_date).toISOString(),
                    currentTime: new Date(now).toISOString(),
                    isExpired: isExpiredNow,
                    isExpiringSoon: isExpired && !isExpiredNow,
                    minutesUntilExpiry: minutesUntilExpiry,
                    secondsUntilExpiry: Math.floor(timeUntilExpiry / 1000),
                    tokenLength: token.access_token?.length || 0,
                    tokenPreview: token.access_token ? token.access_token.substring(0, 20) + '...' : 'missing'
                });
            } else {
                console.log('🔍 Token status check:', {
                    hasToken: true,
                    hasExpiryDate: false,
                    tokenLength: token.access_token?.length || 0
                });
            }
            
            if (!isExpired) {
                console.log('✅ Using stored valid token');
                return token.access_token;
            }
            
            // Token expired or about to expire
            console.log('🔄 Token expired or expiring soon, will request new access');
            // Note: Google Identity Services doesn't support silent refresh
            // We need to request a new token, which may require user consent
        } else {
            console.log('🔍 Token status check:', {
                hasToken: false,
                reason: token ? 'token missing access_token' : 'no token in storage'
            });
        }
        
        // No valid token or token expired, request new access
        console.log('🔐 Requesting Google Drive access...');
        try {
            token = await this.requestDriveAccess();
            
            // Log the new token details
            if (token && token.expiry_date) {
                const timeUntilExpiry = token.expiry_date - Date.now();
                const minutesUntilExpiry = Math.floor(timeUntilExpiry / 60000);
                console.log('✅ New token obtained:', {
                    expiryDate: new Date(token.expiry_date).toISOString(),
                    minutesUntilExpiry: minutesUntilExpiry,
                    expiresIn: token.expires_in,
                    tokenLength: token.access_token?.length || 0
                });
            }
            
            return token.access_token;
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            console.error('❌ Failed to get access token:', {
                error: errorMsg,
                errorType: error instanceof Error ? error.constructor.name : typeof error,
                timestamp: new Date().toISOString()
            });
            // Provide clearer error messages
            if (errorMsg.includes('cancelled') || errorMsg.includes('Sign-in cancelled')) {
                throw new Error('Google Drive sign-in was cancelled. Please try again and sign in to access your files.');
            } else if (errorMsg.includes('popup_closed')) {
                throw new Error('Sign-in popup was closed. Please try again and complete the sign-in process.');
            } else if (errorMsg.includes('access_denied')) {
                throw new Error('Access to Google Drive was denied. Please grant permission to access your files.');
            } else {
                throw new Error(`Failed to get Google Drive access: ${errorMsg}`);
            }
        }
    }

    /**
     * Show Google Picker to select a file
     */
    async showPicker(): Promise<GoogleDriveFile | null> {
        if (!this.pickerApiLoaded) {
            await this.loadGoogleApis();
        }

        // Wait for Picker API to be fully available
        await this.waitForPickerApi();

        // Get access token
        const accessToken = await this.getAccessToken();

        return new Promise((resolve, reject) => {
            try {
                // Verify Picker API is available
                if (!window.google || !window.google.picker) {
                    throw new Error('Google Picker API is not loaded. Please refresh the page and try again.');
                }

                // Create view for audio files
                const docsView = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
                    .setIncludeFolders(false)
                    .setMimeTypes('audio/mpeg,audio/mp4,audio/wav,audio/aac,audio/ogg,audio/flac,audio/x-ms-wma,audio/aiff,audio/3gpp,audio/amr,audio/opus');

                const pickerBuilder = new window.google.picker.PickerBuilder()
                    .addView(docsView)
                    .setOAuthToken(accessToken)
                    .setDeveloperKey(this.apiKey)
                    .setCallback((data: any) => {
                        if (data[window.google.picker.Response.ACTION] === window.google.picker.Action.PICKED) {
                            const file = data[window.google.picker.Response.DOCUMENTS][0];
                            const driveFile: GoogleDriveFile = {
                                id: file.id,
                                name: file.name,
                                mimeType: file.mimeType || 'application/octet-stream',
                                webViewLink: file.url,
                                webContentLink: file.url
                            };
                            console.log('✅ File selected from Google Drive:', driveFile);
                            resolve(driveFile);
                        } else if (data[window.google.picker.Response.ACTION] === window.google.picker.Action.CANCEL) {
                            console.log('❌ Picker cancelled');
                            resolve(null);
                        }
                    });

                // Only set size if Size enum is available
                if (window.google.picker.Size && window.google.picker.Size.DEFAULT) {
                    pickerBuilder.setSize(window.google.picker.Size.DEFAULT);
                }

                const picker = pickerBuilder.build();
                picker.setVisible(true);
            } catch (error) {
                console.error('❌ Error showing Google Picker:', error);
                reject(error);
            }
        });
    }

    /**
     * Wait for Picker API to be fully loaded
     */
    private async waitForPickerApi(): Promise<void> {
        let attempts = 0;
        const maxAttempts = 20;
        
        while (attempts < maxAttempts) {
            if (window.google && 
                window.google.picker && 
                window.google.picker.PickerBuilder &&
                window.google.picker.DocsView &&
                window.google.picker.ViewId) {
                console.log('✅ Picker API fully loaded');
                return;
            }
            
            console.log(`⏳ Waiting for Picker API to load... (attempt ${attempts + 1}/${maxAttempts})`);
            await new Promise(resolve => setTimeout(resolve, 100));
            attempts++;
        }
        
        throw new Error('Google Picker API failed to load after multiple attempts. Please refresh the page.');
    }

    /**
     * Revoke access (logout)
     */
    async revokeAccess(): Promise<void> {
        const token = this.getStoredToken();
        if (token && token.access_token && window.google?.accounts?.oauth2) {
            try {
                window.google.accounts.oauth2.revoke(token.access_token, () => {
                    console.log('✅ Access token revoked');
                });
            } catch (error) {
                console.error('Error revoking access:', error);
            }
        }
        this.clearToken();
        console.log('✅ Google Drive access revoked');
    }

    /**
     * Clear stored token from localStorage (static method for use during logout)
     * This can be called without instantiating the service
     */
    static clearStoredToken(): void {
        const tokenStorageKey = 'google_drive_token';
        try {
            localStorage.removeItem(tokenStorageKey);
            console.log('✅ Google Drive token cleared from localStorage');
        } catch (error) {
            console.error('❌ Error clearing Google Drive token:', error);
        }
    }
}

